import 'C:\\Projects\\genkei\\zumeco\\static\\folder\\ChangeOwnerDialog.tsx.esbuild-plugin-windicss.css';
import { createSharedSelector, createSharedState } from 'preact-shared-state-hook';
import { memo } from 'preact/compat';
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { Doodle, Folder } from '../../server/src/models';
import '../shared/common.css';
import { Alert } from '../shared/components/Alert';
import { DefaultButton } from '../shared/components/DefaultButton';
import { Dialog, showModal } from '../shared/components/Dialog';
import { SubmitButton } from '../shared/components/SubmitButton';
import { useTenantUsersWithoutCurrentUser } from '../shared/data/tenantUsers';
import { i18n } from '../shared/i18n';
import { withProgressBar } from '../shared/utils';
import { setDoodleOwner } from './data/doodles';
import { setFolderOwner, useTenantFolders } from './data/folders';
import './folder.css';
import { getCurrentDoodlesQuery, setCurrentDoodlesQuery } from './folderState';
const [useChangeOwnerTarget, setChangeOwnerTarget] = createSharedState<{
  readonly type: 'doodle';
  readonly doodle: Doodle;
} | {
  readonly type: 'folder';
  readonly folder: Folder;
}>();
const useChangeOwnerAlertMessage = createSharedSelector([useChangeOwnerTarget, useTenantFolders], (target, tenantFolders) => {
  if (!target) {
    return;
  }
  if (target.type === 'doodle') {
    if (target.doodle.parentFolderId && tenantFolders.some(folder => folder.id === target.doodle.parentFolderId)) {
      return i18n.handover.message.handoverTenantSharingDoodle;
    } else {
      return i18n.handover.message.handoverPersonalDoodle;
    }
  }
  if (target.folder.tenantSharing) {
    throw Error();
  } else {
    return i18n.handover.message.handoverPersonalFolder;
  }
});
const onClose = () => setChangeOwnerTarget(undefined);
export const openChangeDoodleOwnerDialog = (doodle: Doodle) => setChangeOwnerTarget({
  type: 'doodle',
  doodle
});
export const openChangeFolderOwnerDialog = (folder: Folder) => setChangeOwnerTarget({
  type: 'folder',
  folder
});
export const ChangeOwnerDialog = memo(() => {
  const target = useChangeOwnerTarget();
  const tenantUsers = useTenantUsersWithoutCurrentUser();
  const alertMessage = useChangeOwnerAlertMessage();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const [newOwnerUserId, setNewOwnerUserId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const close = useCallback(() => dialogRef.current?.close(), []);
  const onSubmit = useCallback((event: Event) => {
    event.preventDefault();
    if (!target || !newOwnerUserId) {
      return;
    }
    setSubmitting(true);
    void withProgressBar(async () => {
      try {
        if (target.type === 'doodle') {
          await setDoodleOwner(target.doodle, newOwnerUserId);
        } else {
          await setFolderOwner(target.folder, newOwnerUserId);
          const query = getCurrentDoodlesQuery();
          query.kind === 'folder' && query.folderId === target.folder.id && void setCurrentDoodlesQuery({
            kind: 'folder',
            folderId: ''
          });
        }
        dialogRef.current?.close();
      } finally {
        setSubmitting(false);
      }
    });
  }, [target, newOwnerUserId]);
  useEffect(() => {
    const isOpen = dialogRef.current?.hasAttribute('open');
    if (target) {
      if (!isOpen) {
        showModal(dialogRef);
        dialogRef.current?.querySelector('select')?.focus();
      }
    } else {
      isOpen && dialogRef.current?.close();
    }
  }, [target]);
  const caption = target?.type === 'doodle' ? i18n.handover.handoverDoodle : i18n.handover.handoverFolder;
  return <Dialog ref={dialogRef} caption={caption} onClose={onClose}>
      {!!alertMessage && <Alert message={alertMessage} />}
      {!!target && <form class="grid grid-cols-[max-content,1fr] gap-0.5em items-center" onSubmit={onSubmit}>
          <label class="text-right opacity-75">{target.type === 'doodle' ? i18n.handover.drawingName : i18n.handover.folderName}:</label>
          <div class="px-2">{target.type === 'doodle' ? target.doodle.filename : target.folder.name}</div>

          <label class="text-right opacity-75">{i18n.handover.newOwner}:</label>
          <select class="w-full px-1 py-0.5" onChange={e => setNewOwnerUserId(e.currentTarget.value)}>
            <option selected disabled hidden children={i18n.handover.selectNewOwner} />
            {tenantUsers?.map(user => <option key={user.uid} value={user.uid} children={user.name} />)}
          </select>

          <div />
          <div class="mt-1em flex justify-end">
            <SubmitButton label={caption} disabled={!newOwnerUserId || submitting} />
            <DefaultButton label={i18n.cancel} onClick={close} />
          </div>
        </form>}
    </Dialog>;
});